package com.eladfinish.bible.contest.features.verseWidget

import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.atDate
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Instant

/**
 * Cuts the day into equal "slots", one verse each. Slots are counted on the local wall clock, from the
 * day the rotation started, so a verse changes at the same clock times whatever the time zone or
 * daylight saving does (on a day with a clock change a slot is skipped or shown twice, which no
 * one will notice).
 *
 * The slot boundaries are those of the clock (an hourly rotation changes on the hour) until the user
 * asks for a new verse: [refreshedAt] then moves them, so that the new verse starts at that moment and
 * the next one a whole interval later. It keeps the move in the config's `phaseSeconds` (how far the
 * boundaries lie from the clock's) and `slotOffset` (the verses skipped by refreshing).
 */
object VerseWidgetSlots {

    private const val SECONDS_PER_DAY = 86_400L
    private const val MINUTES_PER_DAY = 24 * 60

    fun slotsPerDay(intervalMinutes: Int): Int = MINUTES_PER_DAY / intervalMinutes

    private fun intervalSeconds(config: VerseWidgetConfig): Long = config.intervalMinutes * 60L

    /** Seconds of the local wall clock since midnight of the day the rotation started. */
    private fun secondsSinceStart(instant: Instant, config: VerseWidgetConfig, timeZone: TimeZone): Long {
        val local = instant.toLocalDateTime(timeZone)
        return (local.date.toEpochDays() - config.anchorEpochDay) * SECONDS_PER_DAY +
                local.hour * 3_600L + local.minute * 60L + local.second
    }

    /** The slot [instant] falls in; never negative, even when the clock was set back before the start day. */
    fun slotAt(
        instant: Instant,
        config: VerseWidgetConfig,
        timeZone: TimeZone,
    ): Long {
        val seconds = secondsSinceStart(instant, config, timeZone) - config.phaseSeconds
        return maxOf(seconds.floorDiv(intervalSeconds(config)) + config.slotOffset, 0L)
    }

    /** The first moment of [slot]. */
    fun startOf(
        slot: Long,
        config: VerseWidgetConfig,
        timeZone: TimeZone,
    ): Instant {
        val seconds = (slot - config.slotOffset) * intervalSeconds(config) + config.phaseSeconds
        val secondOfDay = seconds.mod(SECONDS_PER_DAY).toInt()
        return LocalTime(hour = secondOfDay / 3_600, minute = secondOfDay % 3_600 / 60, second = secondOfDay % 60)
            .atDate(LocalDate.fromEpochDays(config.anchorEpochDay + seconds.floorDiv(SECONDS_PER_DAY)))
            .toInstant(timeZone)
    }

    /** The local date [slot] falls on. */
    fun dateOf(
        slot: Long,
        config: VerseWidgetConfig,
    ): LocalDate {
        val seconds = (slot - config.slotOffset) * intervalSeconds(config) + config.phaseSeconds
        return LocalDate.fromEpochDays(config.anchorEpochDay + seconds.floorDiv(SECONDS_PER_DAY))
    }

    /** The first slot that starts on [date]. */
    fun firstSlotOf(
        date: LocalDate,
        config: VerseWidgetConfig,
    ): Long {
        val midnight = (date.toEpochDays() - config.anchorEpochDay) * SECONDS_PER_DAY
        val interval = intervalSeconds(config)
        // rounded up: the slot that began before midnight belongs to the day before
        val slotsBefore = -((config.phaseSeconds - midnight).floorDiv(interval))
        return slotsBefore + config.slotOffset
    }

    /**
     * [config] after the user asked for a new verse at [instant]: the next verse of the rotation shows now, and
     * the one after it comes a whole interval from now, not when the clock's boundary would have brought it.
     */
    fun refreshedAt(
        instant: Instant,
        config: VerseWidgetConfig,
        timeZone: TimeZone,
    ): VerseWidgetConfig {
        val interval = intervalSeconds(config)
        val seconds = secondsSinceStart(instant, config, timeZone)
        val phase = seconds.mod(interval)
        val slotWithoutOffset = (seconds - phase).floorDiv(interval)
        return config.copy(
            phaseSeconds = phase.toInt(),
            slotOffset = slotAt(instant, config, timeZone) + 1 - slotWithoutOffset,
        )
    }
}
